
import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import {
  ModeState,
  ModeAction,
  ModeContextValue,
  AppMode,
} from './types/mode.types';
import {
  createInitialModeState,
  MODE_STORAGE_KEY,
} from './data/mode.constants';

function modeReducer(state: ModeState, action: ModeAction): ModeState {
  switch (action.type) {
    case 'SET_MODE':
      return {
        ...state,
        currentMode: action.payload,
        transitionTimestamp: Date.now(),
      };
    case 'SET_QUICK_CONFIG':
      return {
        ...state,
        quickModeConfig: {
          ...state.quickModeConfig,
          ...action.payload,
        },
      };
    case 'SET_LAST_TAB':
      return {
        ...state,
        lastAdvancedTab: action.payload,
      };
    case 'RESET':
      return createInitialModeState();
    default:
      return state;
  }
}

const ModeContext = createContext<ModeContextValue | null>(null);

// Fix: explicitly allow optional children to fix JSX mapping issue in some TypeScript environments
export function ModeProvider({ children }: { children?: React.ReactNode }) {
  const [state, dispatch] = useReducer(modeReducer, null, () => {
    try {
      const stored = localStorage.getItem(MODE_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...createInitialModeState(), ...parsed };
      }
    } catch (e) {
      console.warn('Failed to load mode state:', e);
    }
    return createInitialModeState();
  });

  useEffect(() => {
    localStorage.setItem(MODE_STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const isQuickMode = state.currentMode === 'quick';
  const isAdvancedMode = state.currentMode === 'advanced';

  const toggleMode = useCallback(() => {
    dispatch({
      type: 'SET_MODE',
      payload: state.currentMode === 'quick' ? 'advanced' : 'quick',
    });
  }, [state.currentMode]);

  const setMode = useCallback((mode: AppMode) => {
    dispatch({ type: 'SET_MODE', payload: mode });
  }, []);

  const value = useMemo<ModeContextValue>(() => ({
    state,
    dispatch,
    isQuickMode,
    isAdvancedMode,
    toggleMode,
    setMode,
  }), [state, isQuickMode, isAdvancedMode, toggleMode, setMode]);

  return (
    <ModeContext.Provider value={value}>
      {children}
    </ModeContext.Provider>
  );
}

export function useMode(): ModeContextValue {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
}